import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { gamificationService } from '../../../lib/gamificationService';

const LeaderboardPanel = ({ leaderboards, userRank, gymId, userId, onRefresh }) => {
  const [selectedType, setSelectedType] = useState('points');
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [filteredLeaderboard, setFilteredLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);

  const leaderboardTypes = [
    { id: 'points', label: 'Points', icon: 'Award' },
    { id: 'attendance', label: 'Attendance', icon: 'Calendar' },
    { id: 'referrals', label: 'Referrals', icon: 'Users' },
    { id: 'challenges', label: 'Challenges', icon: 'Target' },
    { id: 'workouts', label: 'Workouts', icon: 'Dumbbell' }
  ];

  const timePeriods = [
    { id: 'daily', label: 'Daily' },
    { id: 'weekly', label: 'Weekly' },
    { id: 'monthly', label: 'Monthly' },
    { id: 'all_time', label: 'All Time' }
  ];

  useEffect(() => {
    loadLeaderboard();
  }, [selectedType, selectedPeriod, gymId]);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const { data } = await gamificationService?.getLeaderboard(
        gymId,
        selectedType,
        selectedPeriod,
        50
      );
      setFilteredLeaderboard(data || []);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankColor = (rank) => {
    if (rank === 1) return 'text-yellow-600';
    if (rank === 2) return 'text-gray-400';
    if (rank === 3) return 'text-orange-600';
    return 'text-gray-600';
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return 'Crown';
    if (rank === 2) return 'Medal';
    if (rank === 3) return 'Award';
    return null;
  };

  const getRankChangeIcon = (change) => {
    if (change > 0) return { icon: 'TrendingUp', color: 'text-green-600' };
    if (change < 0) return { icon: 'TrendingDown', color: 'text-red-600' };
    return { icon: 'Minus', color: 'text-gray-400' };
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Leaderboard Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Leaderboard Type
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {leaderboardTypes?.map((type) => (
                <button
                  key={type?.id}
                  onClick={() => setSelectedType(type?.id)}
                  className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                    selectedType === type?.id
                      ? 'border-orange-600 bg-orange-50 text-orange-600' :'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <Icon name={type?.icon} className="w-4 h-4" />
                  <span className="text-sm font-medium">{type?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Time Period */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Period
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {timePeriods?.map((period) => (
                <button
                  key={period?.id}
                  onClick={() => setSelectedPeriod(period?.id)}
                  className={`px-4 py-2 rounded-lg border-2 transition-all ${
                    selectedPeriod === period?.id
                      ? 'border-orange-600 bg-orange-50 text-orange-600' :'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <span className="text-sm font-medium">{period?.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* User's Rank Card */}
      {userRank && (
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Your Current Rank</p>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-4xl font-bold">#{userRank?.rank_position}</span>
                {userRank?.rank_change !== 0 && (
                  <div className="flex items-center gap-1">
                    <Icon
                      name={getRankChangeIcon(userRank?.rank_change)?.icon}
                      className={`w-5 h-5 ${userRank?.rank_change > 0 ? 'text-green-300' : 'text-red-300'}`}
                    />
                    <span className="text-sm font-medium">
                      {Math.abs(userRank?.rank_change)} {userRank?.rank_change > 0 ? 'up' : 'down'}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-orange-100 text-sm font-medium">Your Score</p>
              <p className="text-4xl font-bold mt-2">{userRank?.score_value?.toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {leaderboardTypes?.find(t => t?.id === selectedType)?.label} Leaderboard - {timePeriods?.find(p => p?.id === selectedPeriod)?.label}
          </h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
          </div>
        ) : filteredLeaderboard?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Trophy" className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No leaderboard data available for this period</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Change
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLeaderboard?.map((entry) => {
                  const isCurrentUser = entry?.user_id === userId;
                  const rankIcon = getRankIcon(entry?.rank_position);
                  const changeInfo = getRankChangeIcon(entry?.rank_change || 0);

                  return (
                    <tr
                      key={entry?.id}
                      className={isCurrentUser ? 'bg-orange-50' : 'hover:bg-gray-50'}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {rankIcon && (
                            <Icon
                              name={rankIcon}
                              className={`w-5 h-5 ${getRankColor(entry?.rank_position)}`}
                            />
                          )}
                          <span className={`text-lg font-bold ${getRankColor(entry?.rank_position)}`}>
                            #{entry?.rank_position}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                              <span className="text-orange-600 font-semibold">
                                {entry?.user?.full_name?.charAt(0)?.toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {entry?.user?.full_name}
                              {isCurrentUser && (
                                <span className="ml-2 px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                                  You
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-gray-900">
                          {entry?.score_value?.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Icon
                            name={changeInfo?.icon}
                            className={`w-4 h-4 ${changeInfo?.color}`}
                          />
                          <span className={`text-sm font-medium ${changeInfo?.color}`}>
                            {entry?.rank_change === 0 ? '-' : Math.abs(entry?.rank_change || 0)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaderboardPanel;